import { type RequiredFileModel, toUuidArray, type UUID } from "@overmap-ai/common"
import { BaseOfflineFileModelApiService } from "@overmap-ai/core"
import { UUIDFile, UUIDPromise } from "@overmap-ai/forms"

import { AppSDK, sdk } from "../sdk"
import type { RootState } from "../store"
import { uuid } from "./string"

export function readFileAsArrayBuffer(file: File | Blob): Promise<ArrayBuffer> {
	return new Promise((resolve, reject) => {
		const reader = new FileReader()
		// Provide an onloadend callback for this instance of FileReader
		// This is called once reader.readAsArrayBuffer() is done
		reader.onloadend = () => {
			const fileResult = reader.result as ArrayBuffer
			if (!fileResult) {
				reject()
				return
			}
			resolve(fileResult)
		}
		// calling reader.readAsArrayBuffer and providing a file should trigger the callback above
		// as soon as readAsArrayBuffer is complete
		reader.readAsArrayBuffer(file)
	})
}

export function getFileDownloadAttributes(filename: string, text: string) {
	const href = "data:text/plain;charset=utf-8," + encodeURIComponent(text)
	return { href, download: filename }
}

export function uuidFilesFromFiles(files: File[]) {
	return files.map((file) => UUIDFile.from(uuid(), file))
}

export const getFilesAndPromisesFromAttachments = <TAttachment extends UUID<RequiredFileModel>>(
	attachments: TAttachment[],
	service: BaseOfflineFileModelApiService<TAttachment["uuid"], TAttachment, RootState, AppSDK>,
) => {
	return attachments.map((attachment) =>
		UUIDPromise.from(
			service
				.getPresignedUrl(attachment.uuid)
				.then((fileUrl) => sdk.files.fetchFileFromUrl(fileUrl, attachment.file_sha1, attachment.file_name))
				.then((file) => UUIDFile.from(attachment.uuid, file)),
			attachment.uuid,
		),
	)
}

export const getFilesAndPromisesToCreate = <TModel extends UUID<RequiredFileModel>>(
	attachments: TModel[],
	filesAndPromises: (UUIDFile | UUIDPromise<UUIDFile>)[],
) => {
	const previousAttachmentUuids = new Set(toUuidArray(attachments))
	return filesAndPromises.filter(({ uuid }) => !previousAttachmentUuids.has(uuid))
}

export const getAttachmentsToDelete = <TModel extends UUID<RequiredFileModel>>(
	attachments: TModel[],
	filesAndPromises: (UUIDFile | UUIDPromise<UUIDFile>)[],
) => {
	const filesAndPromisesUuids = new Set(toUuidArray(filesAndPromises))
	return attachments.filter(({ uuid }) => !filesAndPromisesUuids.has(uuid))
}
